import { ref, watch } from 'vue';
import { sanitizeHtml, isValidHtmlFile, hasUnsafeContent } from '@/features/CampaignsEmail/utils/sanitizeHtml';
export const useHtmlInsertion = (options) => {
    const { template: templateRef, t, toast } = options;
    const insertionMethod = ref(null);
    const htmlFile = ref(null);
    const htmlFileName = ref('');
    const htmlFileSize = ref('');
    const htmlUrl = ref('');
    const loadedHtmlUrl = ref('');
    const htmlInserted = ref(false);
    const loadingHtml = ref(false);
    const fileInputRef = ref(null);
    const clearFileMeta = () => {
        htmlFile.value = null;
        htmlFileName.value = '';
        htmlFileSize.value = '';
        if (fileInputRef.value)
            fileInputRef.value.value = '';
    };
    const clearUrlMeta = () => {
        htmlUrl.value = '';
        loadedHtmlUrl.value = '';
    };
    const reset = () => {
        insertionMethod.value = null;
        clearFileMeta();
        clearUrlMeta();
        htmlInserted.value = false;
        loadingHtml.value = false;
    };
    const openFileDialog = () => {
        fileInputRef.value?.click();
    };
    const setSelectedFile = (file) => {
        htmlFile.value = file;
        htmlFileName.value = file.name;
        const sizeInKB = file.size / 1024;
        htmlFileSize.value = sizeInKB < 1024 ? `${sizeInKB.toFixed(2)} KB` : `${(sizeInKB / 1024).toFixed(2)} MB`;
    };
    const applyHtmlContent = async (htmlContent) => {
        if (!htmlContent || htmlContent.trim().length === 0) {
            toast.error(t('breadcrumb.email.campaigns.toasts.html-empty'));
            return false;
        }
        if (hasUnsafeContent(htmlContent)) {
            toast.warning(t('breadcrumb.email.campaigns.toasts.html-unsafe-content'));
        }
        const sanitizedContent = sanitizeHtml(htmlContent);
        if (sanitizedContent.trim().length < 50) {
            toast.error(t('breadcrumb.email.campaigns.toasts.html-invalid-after-sanitization'));
            return false;
        }
        templateRef.value = sanitizedContent;
        htmlInserted.value = true;
        return true;
    };
    const handleFileChange = (event) => {
        const target = event.target;
        const file = target.files?.[0];
        if (!file)
            return;
        if (!isValidHtmlFile(file.name)) {
            toast.error(t('breadcrumb.email.campaigns.toasts.invalid-html-file'));
            if (fileInputRef.value)
                fileInputRef.value.value = '';
            return;
        }
        // Selecionar arquivo não insere o HTML automaticamente.
        // Apenas guarda o arquivo; a inserção ocorre ao clicar em "Inserir HTML".
        htmlInserted.value = false;
        loadedHtmlUrl.value = '';
        setSelectedFile(file);
    };
    const insertFromSelectedFile = async () => {
        if (!htmlFile.value) {
            toast.error(t('breadcrumb.email.campaigns.toasts.html-empty'));
            return;
        }
        try {
            loadingHtml.value = true;
            const startTime = Date.now();
            const minLoadingTime = 1500;
            const file = htmlFile.value;
            const reader = new FileReader();
            const cleanup = () => {
                reader.onload = null;
                reader.onerror = null;
            };
            reader.onload = async (e) => {
                const htmlContent = e.target?.result;
                const ok = await applyHtmlContent(htmlContent);
                if (!ok) {
                    htmlInserted.value = false;
                }
                else {
                    loadedHtmlUrl.value = '';
                    toast.success(t('breadcrumb.email.campaigns.toasts.html-inserted-success'));
                }
                const elapsedTime = Date.now() - startTime;
                const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
                setTimeout(() => {
                    loadingHtml.value = false;
                    cleanup();
                }, remainingTime + 50);
            };
            reader.onerror = () => {
                toast.error(t('breadcrumb.email.campaigns.toasts.html-read-error'));
                loadingHtml.value = false;
                htmlInserted.value = false;
                cleanup();
            };
            reader.readAsText(file);
        }
        catch {
            toast.error(t('breadcrumb.email.campaigns.toasts.html-process-error'));
            loadingHtml.value = false;
            htmlInserted.value = false;
        }
    };
    const isProbablyHtmlUrl = (url, contentType) => {
        const pathname = url.pathname.toLowerCase();
        const extOk = pathname.endsWith('.html') || pathname.endsWith('.htm');
        const typeOk = !!contentType && contentType.toLowerCase().includes('text/html');
        return extOk || typeOk;
    };
    const loadFromUrl = async () => {
        const raw = htmlUrl.value.trim();
        if (!raw) {
            toast.error(t('breadcrumb.email.campaigns.toasts.html-url-required'));
            return;
        }
        let parsedUrl;
        try {
            parsedUrl = new URL(raw);
        }
        catch {
            toast.error(t('breadcrumb.email.campaigns.toasts.html-url-invalid'));
            return;
        }
        if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
            toast.error(t('breadcrumb.email.campaigns.toasts.html-url-protocol-invalid'));
            return;
        }
        loadingHtml.value = true;
        try {
            const response = await fetch(parsedUrl.toString(), { method: 'GET' });
            if (!response.ok) {
                toast.error(t('breadcrumb.email.campaigns.toasts.html-url-http-error', { status: response.status }));
                return;
            }
            const contentType = response.headers.get('content-type');
            if (!isProbablyHtmlUrl(parsedUrl, contentType)) {
                toast.error(t('breadcrumb.email.campaigns.toasts.html-url-not-html'));
                return;
            }
            const htmlContent = await response.text();
            const ok = await applyHtmlContent(htmlContent);
            if (!ok) {
                htmlInserted.value = false;
                return;
            }
            loadedHtmlUrl.value = parsedUrl.toString();
            clearFileMeta();
            toast.success(t('breadcrumb.email.campaigns.toasts.html-inserted-success'));
        }
        catch {
            toast.error(t('breadcrumb.email.campaigns.toasts.html-url-fetch-error'));
        }
        finally {
            loadingHtml.value = false;
        }
    };
    const removeHtml = () => {
        clearFileMeta();
        clearUrlMeta();
        htmlInserted.value = false;
        loadingHtml.value = false;
        templateRef.value = '';
        toast.success(t('breadcrumb.email.campaigns.toasts.html-removed-success'));
    };
    watch(insertionMethod, (method, old) => {
        if (method === old)
            return;
        htmlInserted.value = false;
        // Ao limpar o método (null), não apagamos o template automaticamente,
        // pois o formulário pode restaurar um HTML já existente (modo edição).
        if (method === null) {
            clearFileMeta();
            clearUrlMeta();
            loadingHtml.value = false;
            return;
        }
        templateRef.value = '';
        if (method === 'upload') {
            clearUrlMeta();
        }
        if (method === 'link') {
            clearFileMeta();
        }
    });
    return {
        insertionMethod,
        htmlFile,
        htmlFileName,
        htmlFileSize,
        htmlUrl,
        loadedHtmlUrl,
        htmlInserted,
        loadingHtml,
        fileInputRef,
        openFileDialog,
        handleFileChange,
        insertFromSelectedFile,
        loadFromUrl,
        removeHtml,
        reset,
    };
};
